/**
 * MFA Challenge Component
 *
 * Shown during sign-in when Firebase throws auth/multi-factor-auth-required.
 * Uses the MultiFactorResolver from AuthContext to complete sign-in after
 * the user enters the SMS verification code.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Shield,
  AlertCircle,
  Loader2,
} from 'lucide-react';

export function MFAChallenge(): JSX.Element {
  const { mfaResolver, resolveMfaSignIn, clearMfaState } = useAuth();

  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  const initRecaptcha = useCallback(() => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }

    if (recaptchaContainerRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
        size: 'invisible',
      });
    }
  }, []);

  // Get the masked phone number hint from the resolver
  const getPhoneHint = (): string => {
    if (!mfaResolver) return '';
    const hints = mfaResolver.hints;
    const phoneHint = hints.find(
      (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID
    );
    if (phoneHint && 'phoneNumber' in phoneHint) {
      return (phoneHint as any).phoneNumber || '';
    }
    return '';
  };

  // Auto-send the code when the component mounts
  useEffect(() => {
    initRecaptcha();

    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, [initRecaptcha]);

  useEffect(() => {
    if (mfaResolver && recaptchaVerifierRef.current && !codeSent && !isSending) {
      sendVerificationCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaResolver, codeSent]);

  const sendVerificationCode = async () => {
    if (!mfaResolver || isSending) return;

    setError(null);
    setIsSending(true);

    try {
      if (!recaptchaVerifierRef.current) {
        initRecaptcha();
      }

      const phoneHintIndex = mfaResolver.hints.findIndex(
        (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      );

      if (phoneHintIndex === -1) {
        setError('No phone factor found. Please contact support.');
        return;
      }

      const phoneInfoOptions = {
        multiFactorHint: mfaResolver.hints[phoneHintIndex],
        session: mfaResolver.session,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierRef.current!
      );

      setVerificationId(verId);
      setCodeSent(true);
    } catch (err: any) {
      console.error('Error sending MFA challenge code:', err);
      if (err?.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Failed to send verification code. Please try again.');
      }
      initRecaptcha();
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationId || verificationCode.length !== 6) return;

    setError(null);
    setIsVerifying(true);

    try {
      await resolveMfaSignIn(verificationId, verificationCode);
      // Auth state listener will handle the rest
    } catch (err: any) {
      console.error('Error verifying MFA code:', err);
      if (err?.code === 'auth/invalid-verification-code') {
        setError("That code didn't match. Please try again.");
      } else if (err?.code === 'auth/code-expired') {
        setError('The verification code has expired. Please request a new one.');
        setCodeSent(false);
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const phoneHint = getPhoneHint();

  return (
    <div className="min-h-screen flex items-center justify-center bg-airy">
      <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-card border border-soft-neutral">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-evergreen" />
          </div>
          <h1 className="text-2xl font-bold text-ink">Verify Your Identity</h1>
          <p className="text-muted text-sm mt-2">
            {codeSent ? (
              <>
                Enter the 6-digit code sent to{' '}
                <span className="font-semibold text-ink">{phoneHint || 'your phone'}</span>
              </>
            ) : isSending ? (
              'Sending verification code...'
            ) : (
              'Preparing verification...'
            )}
          </p>
        </div>

        {/* Loading state while sending */}
        {isSending && !codeSent && (
          <div className="flex justify-center py-6">
            <Loader2 className="w-8 h-8 animate-spin text-evergreen" />
          </div>
        )}

        {/* Verification form */}
        {codeSent && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="label">Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '');
                  if (val.length <= 6) setVerificationCode(val);
                }}
                placeholder="000000"
                className="input text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                required
                disabled={isVerifying}
                autoFocus
              />
            </div>

            {error && (
              <div className="alert alert-error flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full flex items-center justify-center gap-2"
              disabled={isVerifying || verificationCode.length !== 6}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Sign In'
              )}
            </button>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => {
                  clearMfaState();
                }}
                className="text-sm text-muted hover:text-slate-ink hover:underline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setVerificationCode('');
                  setError(null);
                  setCodeSent(false);
                  sendVerificationCode();
                }}
                className="text-sm text-evergreen hover:underline"
                disabled={isSending}
              >
                Resend code
              </button>
            </div>
          </form>
        )}

        {/* Error state when not sending */}
        {!isSending && !codeSent && error && (
          <div className="space-y-4">
            <div className="alert alert-error flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
            <div className="flex gap-3">
              <button
                onClick={clearMfaState}
                className="btn-secondary flex-1"
              >
                Back to Sign In
              </button>
              <button
                onClick={sendVerificationCode}
                className="btn-primary flex-1"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invisible reCAPTCHA container */}
      <div ref={recaptchaContainerRef} id="recaptcha-container-challenge" />
    </div>
  );
}

export default MFAChallenge;
